import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { useDomainTranslation } from "@/domain/_shared/i18n/use-domain-translation";
import { Datatable } from "@/domain/view/components/datatable/datatable";
import { DatatableRowDataWithCacheKey, defaultDatatableState } from "@/domain/view/components/datatable/datatable-types";
import type { Workspace } from "@/domain/workspace/models/workspace-model";
import { Row } from "@tanstack/react-table";

import type { DataRequest } from "../../models/data-request.model";

import { COLUMN_ID_STATUS, useDataRequestColumns } from "./use-data-request-columns";

const VIEW_KEY = "data-request-list";
const COLUMN_ID_TITLE = "title";

interface DataRequestDatatableProps {
  dataRequestList: DataRequest[];
  workspace: Workspace;
}

export function DataRequestDatatable({ dataRequestList, workspace }: DataRequestDatatableProps) {
  const { t } = useDomainTranslation("data-request");
  const navigate = useNavigate();
  const columns = useDataRequestColumns();

  const handleRowClick = useCallback(
    (row: Row<DatatableRowDataWithCacheKey<DataRequest>>) => {
      const original = row.original;
      if ("id" in original && "title" in original) {
        navigate(`/${workspace.workspaceUrl}/data-requests/${original.id}`);
      }
    },
    [navigate, workspace.workspaceUrl],
  );

  if (dataRequestList.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
        {t("list.empty")}
      </div>
    );
  }

  return (
    <div className="relative flex min-h-0 flex-1 items-stretch">
      <Datatable<DataRequest>
        viewKey={VIEW_KEY}
        workspaceId={workspace.id}
        data={dataRequestList}
        columns={columns}
        initialState={{
          ...defaultDatatableState,
          showColumnHeaders: true,
          sectionColumnId: COLUMN_ID_STATUS,
          sorting: [{ id: COLUMN_ID_TITLE, desc: false }],
        }}
        debugTable={false}
        synchToApi={false}
        initWithUrlParams={false}
        onRowClick={handleRowClick}
        config={{ quicksearch: true }}
        getColumnCanGlobalFilter={(column) => [COLUMN_ID_TITLE].includes(column.id)}
        globalFilterFn={(row, columnId, value) => {
          if (!value || value === "") return true;
          const original = row.original;
          if (!("title" in original)) return true;
          if (columnId === COLUMN_ID_TITLE) {
            return (original as DataRequest).title.toLowerCase().includes(value.toLowerCase());
          }
          return false;
        }}
      />
    </div>
  );
}
